<script lang="ts">
    import Modal from "@/components/Modal.svelte";
    import {Button} from "@/components/ui/button";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import type Blocks from "@/schemas/public/Blocks";
    import type {Field, Customer} from "@/types/types";
    import {useForm} from "@/lib/precognition.svelte";
    import {toast} from "svelte-sonner";
    import {SvelteMap} from "svelte/reactivity";
    import MultiRelated from "@/components/Inputs/MultiRelated.svelte";
    import Checkbox from "@/components/Inputs/Checkbox.svelte";
    import Select from "@/components/Inputs/Select.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import JobTable from "@/components/jobs/JobTable.svelte";
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import { parseFieldOptions } from '@/lib/fieldUtils';
    import { Building2, User } from "lucide-svelte";

    type Props = {
        blocks: (Blocks & { fields: Field[] })[];
        show: boolean;
        customer: Customer;
        customerTypeOptions?: Record<string, string>;
        availableOrganisations?: { id: string; name: string }[];
    };

    let {blocks, show = $bindable(), customer, customerTypeOptions = {}, availableOrganisations = []}: Props = $props();

    let formInner: Record<string, any> = {
        type: customer.type ?? 'contact',
        organisation_id: customer.organisation_id ?? null,
    };
    for (const block of blocks) {
        for (const field of block.fields) {
            formInner[field.column_name] = field.value;
        }
    }
    let form = $state(useForm(formInner, 'put', route('customers.update', { customer: customer.id })));

    const isOrganisation = $derived($form.type === 'organisation');
    const isContact = $derived($form.type === 'contact');

    // Organisation options for select
    const organisationOptions = $derived([
        { label: 'None', value: '' },
        ...availableOrganisations.map(org => ({ label: org.name, value: org.id }))
    ]);

    function submit(e: Event) {
        $form.processing = true;
        e.preventDefault();
        $form.put(route('customers.update', {customer: customer.id}), {
            onSuccess: () => {
                close();
                $form.reset();
                toast.success("Customer edited successfully!");
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error("Error editing customer. " + message as string);
                }
            },
            onFinish: () => {
                $form.processing = false;
            }
        });
    }

    let jobLabel: string | null | undefined = $state(null);
    let isSelectJobOpen = $state(false);
    let selectedRelatedJobs = $state(new SvelteMap());

    // Initialize related jobs from customer data
    if (customer.related_jobs) {
        customer.related_jobs.forEach(c => {
            selectedRelatedJobs.set(c.id, c.title);
        });
        $form.related_jobs = selectedRelatedJobs.keys().toArray();
        jobLabel = Array.from(selectedRelatedJobs.values()).join(", ");
    }

    function close() {
        show = false;
    }
</script>

{#if show}
    <Modal title={isOrganisation ? 'Edit Organisation' : 'Edit Contact'} onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                <!-- Type Selection -->
                <FormBlock label="Customer Type">
                    <div class="flex gap-4">
                        <button
                            type="button"
                            onclick={() => $form.type = 'contact'}
                            class="flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-colors {isContact ? 'border-primary bg-primary/10' : 'border-muted hover:border-muted-foreground/50'}"
                        >
                            <User class="h-5 w-5" />
                            <span class="font-medium">Contact</span>
                        </button>
                        <button
                            type="button"
                            onclick={() => $form.type = 'organisation'}
                            class="flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-colors {isOrganisation ? 'border-primary bg-primary/10' : 'border-muted hover:border-muted-foreground/50'}"
                        >
                            <Building2 class="h-5 w-5" />
                            <span class="font-medium">Organisation</span>
                        </button>
                    </div>
                </FormBlock>

                <!-- Organisation Selection (for contacts only) -->
                {#if isContact && availableOrganisations.length > 0}
                    <FormBlock label="Organisation">
                        <Select
                            label="Belongs to Organisation"
                            bind:value={$form.organisation_id}
                            options={organisationOptions}
                            error={$form.errors.organisation_id}
                        />
                    </FormBlock>
                {/if}

                {#each blocks as block}
                    <FormBlock label={block.label}>
                        {#each block.fields as field}
                            {#if field.editable}
                                {#if field.ui_type === 'multi_related'}
                                    <MultiRelated
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                        bind:isOpen={isSelectJobOpen}
                                        bind:valLabel={jobLabel}
                                    >
                                        <JobTable
                                            view="multi_related"
                                            onRowClick={(row) => {}}
                                            bind:selectedJobs={selectedRelatedJobs}
                                            onSubmitClick={() => {
                                                $form.related_jobs = selectedRelatedJobs.keys().toArray();
                                                jobLabel = Array.from(selectedRelatedJobs.values()).join(", ");
                                                isSelectJobOpen = false;
                                            }}
                                        />
                                    </MultiRelated>
                                {:else if field.ui_type === 'checkbox'}
                                    <Checkbox
                                        bind:checked={$form[field.column_name]}
                                        label={field.label}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'select'}
                                    <Select
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        options={parseFieldOptions(field)}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'textarea'}
                                    <Textarea
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'datetime'}
                                    <DateTime
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else}
                                    <Input
                                        field={field}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                        onkeydown={() => {}}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <div class="mt-5 flex justify-end gap-2">
                    <Button type="button" variant="outline" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$form.processing}>
                        {#if $form.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}